import React from "react";
import { toast } from "react-toastify";
import {
  Download,
  ExternalLink,
  Tag,
  FileText,
  Video,
  BookOpen,
  HelpCircle,
  Sparkles,
} from "lucide-react";

const TYPE_CONFIG = {
  Notes: {
    bg: "bg-blue-50 text-blue-700 border-blue-100",
    icon: FileText,
    badgeBg: "bg-blue-600",
  },
  "Question Paper": {
    bg: "bg-emerald-50 text-emerald-700 border-emerald-100",
    icon: HelpCircle,
    badgeBg: "bg-emerald-600",
  },
  Youtube: {
    bg: "bg-rose-50 text-rose-700 border-rose-100",
    icon: Video,
    badgeBg: "bg-rose-600",
  },
  "Important Courses": {
    bg: "bg-purple-50 text-purple-700 border-purple-100",
    icon: BookOpen,
    badgeBg: "bg-purple-600",
  },
  Default: {
    bg: "bg-gray-50 text-gray-700 border-gray-100",
    icon: Sparkles,
    badgeBg: "bg-gray-600",
  },
};

export function ResourceCard({ item }) {
  const isDownloadable =
    item.type === "Notes" || item.type === "Question Paper";

  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.Default;
  const TypeIcon = config.icon;

  const handleAction = () => {
    if (!item.link) {
      toast.error("Resource link not available");
      return;
    }

    if (isDownloadable) {
      const a = document.createElement("a");
      a.href = item.link;
      a.download = item.title || "resource";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Download started!");
    } else {
      window.open(item.link, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="group bg-white rounded-2xl p-5 shadow-xs border border-gray-100/90 hover:shadow-md hover:border-purple-200 transition-all duration-300 flex flex-col justify-between">
      <div>
        {/* Top Badges */}
        <div className="flex items-center justify-between mb-3.5">
          <span
            className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.bg}`}
          >
            <TypeIcon size={13} />
            <span>{item.type || "Resource"}</span>
          </span>

          <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
            <Tag size={13} className="text-gray-400" />
            <span className="truncate max-w-[110px]">
              {item.subject || "General"}
            </span>
          </span>
        </div>

        {/* Title */}
        <h3 className="text-base font-bold text-gray-900 group-hover:text-purple-700 transition-colors line-clamp-2 mb-2 leading-snug">
          {item.title}
        </h3>

        {/* Description */}
        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-4">
          {item.description || "Comprehensive study materials and notes for exam preparation."}
        </p>
      </div>

      {/* Action Footer Button */}
      <div className="pt-3 border-t border-gray-100 mt-auto">
        <button
          onClick={handleAction}
          className={`w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-xs ${
            isDownloadable
              ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200"
              : "bg-purple-600 hover:bg-purple-700 text-white shadow-purple-200"
          }`}
        >
          {isDownloadable ? (
            <>
              <Download size={15} />
              <span>Download Notes</span>
            </>
          ) : (
            <>
              <ExternalLink size={15} />
              <span>Open Learning Link</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ResourceCard;